/** Scraper Health (v1 scraper-health.js scraperHealthPanel/healthLoad). Read-only:
 * fetch-cost card (direct vs Scrapfly, 7d), incident stat tiles, and per-incident
 * cards with the kind pill, auto-retried badge, AI diagnosis, and consecutive-fail
 * count. Data comes from the PHASE20 read policies. */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchScraperHealth, fetchFetchStats, isOpen, incidentHue, remediateIncident,
  type ScraperIncident, type FetchStats,
} from "./api";

function fmtWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function ScraperHealthPage({
  loadHealth,
  loadStats,
  token = null,
}: {
  loadHealth: () => Promise<ScraperIncident[]>;
  loadStats: () => Promise<FetchStats>;
  token?: string | null;
}) {
  // "Fix it" per incident (Tom 2026-08-25) — run the safe remediation + show
  // what it did, right on the card.
  const [fixing, setFixing] = useState<string | null>(null);
  const [fixMsg, setFixMsg] = useState<Record<string, string>>({});
  const fixIt = async (r: ScraperIncident): Promise<void> => {
    if (fixing) return;
    setFixing(r.id);
    setFixMsg((m) => ({ ...m, [r.id]: "Working…" }));
    try {
      const res = await remediateIncident(token, { source: r.source, kind: r.kind, county: r.county });
      const parts = [...(res.did ?? []), ...(res.needs ?? [])];
      setFixMsg((m) => ({ ...m, [r.id]: parts.length ? parts.join(" · ") : "Nothing to do." }));
    } catch (e) {
      setFixMsg((m) => ({ ...m, [r.id]: "Couldn't run the fix — " + (e instanceof Error ? e.message : "try again") }));
    } finally {
      setFixing(null);
    }
  };
  const [rows, setRows] = useState<ScraperIncident[] | null>(null);
  // null = never successfully read (render "—", NOT nothing, and retry).
  // THE RULE, after three rounds of this bar vanishing on Tom (2026-08-15/16):
  // the bandwidth section ALWAYS renders. Numbers when known, em dashes when
  // not — it is never allowed to disappear. A failed read auto-retries.
  const [stats, setStats] = useState<FetchStats | null>(null);
  const [loading, setLoading] = useState(false);
  // A failed read used to collapse into rows=[], which renders as
  // "✓ All healthy — no scraper incidents reported." The one page whose job is
  // to tell you the scrapers are broken stated the opposite, most confidently,
  // exactly when the backend was down. Empty and unknown are not the same.
  const [err, setErr] = useState<string | null>(null);
  const retries = useRef(0);
  const retryTimer = useRef<number | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setErr(null);
    void loadHealth()
      .then((r) => { setRows(r); setErr(null); })
      .catch((e: unknown) => { setErr(e instanceof Error ? e.message : "could not reach the backend"); })
      .finally(() => setLoading(false));
    const tryStats = (): void => {
      void loadStats().then((s) => { if (s) setStats(s); retries.current = 0; }).catch(() => {
        // token gap / transient — retry quietly, bounded (10s × 6 ≈ one minute)
        if (retries.current < 6) {
          retries.current += 1;
          if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
          retryTimer.current = window.setTimeout(tryStats, 10_000);
        }
      });
    };
    tryStats();
  }, [loadHealth, loadStats]);

  useEffect(() => {
    refresh();
    return () => { if (retryTimer.current !== null) window.clearTimeout(retryTimer.current); };
  }, [refresh]);

  const totalF = stats ? stats.direct + stats.scrapfly : 0;
  const pctFree = stats && totalF ? Math.round((stats.direct / totalF) * 100) : 0;
  const open = (rows ?? []).filter(isOpen).length;
  const degraded = (rows ?? []).filter((r) => r.status === "degraded").length;

  return (
    // "schealth" FIRST: the app-wide scroll restore keys on classList[0], and
    // "leads" is shared with Notifications/Expenses/Properties — a scroll
    // offset saved on any of them replayed onto this page and hid the
    // bandwidth bar ~150px above the restored position (per-tab, which is why
    // a fresh tab never reproduced it). A unique first class = a unique key.
    <div className="schealth leads exppage">
      <div className="exphead">
        <h1>Scraper Health</h1>
        <div className="expacts">
          <button className="btn slim" onClick={refresh} disabled={loading}>{loading ? "Refreshing…" : "↻ Refresh"}</button>
        </div>
      </div>

      <div className="expqbo">
        Auto-detected scraper issues, what broke, and the monitor's AI diagnosis. Transient failures are retried automatically.
        Scraping itself runs server-side; this is the read-only health view.
      </div>

      {/* ALWAYS rendered — never hidden (see THE RULE above). */}
      <div className="schealthcost">
        <div className="schcosttiles">
          <div className="dkpi"><span className="lbl">Direct (free)</span><strong className="good">{stats ? stats.direct.toLocaleString() : "—"}</strong></div>
          {/* fetch_stats.scrapfly is the legacy COLUMN name; since 2026-07 it counts
    residential-proxy (Decodo) fetches — Scrapfly itself is cancelled. */}
<div className="dkpi"><span className="lbl">Via residential proxy (paid)</span><strong>{stats ? stats.scrapfly.toLocaleString() : "—"}</strong></div>
          <div className="dkpi"><span className="lbl">Fetched free</span><strong>{stats && totalF ? pctFree + "%" : "—"}</strong></div>
        </div>
        <div className="schcostbar"><span className="free" style={{ width: (stats ? pctFree : 0) + "%" }} /><span className="paid" style={{ width: (stats ? 100 - pctFree : 0) + "%" }} /></div>
        <div className="sub">
          {stats
            ? "Only residential-proxy fetches cost bandwidth (last 7 days). Direct fetches — InvestorLift, plain county APIs — are free."
            : "Fetch stats unreadable right now — retrying automatically. This is a read hiccup, not an all-clear."}
        </div>
      </div>

      {/* rows === null means "not read yet / read failed" — show em dashes, not
          a green zero. A zero here reads as an all-clear we cannot vouch for. */}
      <div className="dkpis exptiles">
        <div className="dkpi"><span className="lbl">Open incidents</span><strong className={rows === null ? "" : open ? "owed" : "good"}>{rows === null ? "—" : open}</strong></div>
        <div className="dkpi"><span className="lbl">Degraded now</span><strong>{rows === null ? "—" : degraded}</strong></div>
        <div className="dkpi"><span className="lbl">Recent (total)</span><strong>{rows === null ? "—" : rows.length}</strong></div>
      </div>

      {err ? (
        <div className="list">
          <div className="expempty">
            ⚠️ Could not read scraper health — {err}. This is NOT an all-clear: the
            scrapers may be fine or may be down, and this page cannot currently tell.
            <div><button className="btn slim" onClick={refresh} disabled={loading}>Try again</button></div>
          </div>
        </div>
      ) : rows === null ? (
        <div className="list"><div className="expempty">Loading incidents…</div></div>
      ) : rows.length === 0 ? (
        <div className="list"><div className="expempty">✓ All healthy — no scraper incidents reported.</div></div>
      ) : (
        <div className="schlist">
          {rows.map((r) => (
            <div key={r.id} className={"schcard hue-" + incidentHue(r)}>
              <div className="schhead">
                <span className={"schkind hue-" + incidentHue(r)}>{r.status === "resolved" ? "Resolved" : (r.kind || "incident").replace(/_/g, " ")}</span>
                <strong className="schwhere">{r.whereTxt || r.source || "scraper"}</strong>
                {r.county ? <span className="expcat">{r.county}</span> : null}
                {r.retryDone ? <span className="schretry">↻ auto-retried</span> : null}
                <span className="schwhen">{fmtWhen(r.createdAt)}</span>
              </div>
              {r.whatTxt ? <div className="schwhat">{r.whatTxt}</div> : null}
              {r.diagnosis ? <div className="schdiag"><span className="schdiaglbl">✨ AI diagnosis:</span> {r.diagnosis}</div> : null}
              {r.consecutive > 1 ? <div className="schfail">Failed {r.consecutive} times in a row.</div> : null}
              {isOpen(r) ? (
                <div className="schfix">
                  <button className="btn slim" disabled={fixing === r.id} onClick={() => void fixIt(r)}>
                    {fixing === r.id ? "Fixing…" : "🔧 Try to fix it"}
                  </button>
                  {fixMsg[r.id] ? <span className="schfixmsg">{fixMsg[r.id]}</span> : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
